import React from 'react';
import { AopLink, Card, Confidence, InternalCard, Pill, StatCard, StatusPill, WarnRow } from '../ui';
import { measurementCaveats, reporting } from '../copData';
import { exportJson, stamp } from '../copExport';
import { useCop } from '../CopApp';

export default function IntelligenceReporting() {
	const { navigate, showToast, isInternal, reportDateRange, reportDateRangeLabel } = useCop();

	const downloadReport = () => {
		exportJson(`cop-intelligence-report-${stamp()}.json`, {
			generated_at: new Date().toISOString(),
			date_range: reportDateRange,
			date_range_label: reportDateRangeLabel,
			...reporting,
		});
		showToast('Full report downloaded (JSON)');
	};

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 280 }}>
					<h1 className="cop-page-title">Intelligence Reporting</h1>
					<div className="cop-page-sub">{reportDateRangeLabel} · holdout-measured where marked · projections labeled as projections</div>
				</div>
				<button className="cop-btn" onClick={() => showToast('Executive review scheduled')}>Schedule Executive Review</button>
				<button className="cop-btn primary" onClick={downloadReport}>Download Full Report</button>
			</div>

			<div className="cop-stats-row">
				{reporting.kpis.map((k) => (
					<StatCard key={k.label} {...k} bars />
				))}
			</div>

			<Card title="Executive Intelligence Summary" icon="✦" className="cop-brief">
				<p style={{ margin: 0, color: 'var(--text-sec)', fontSize: 13, lineHeight: 1.7 }}>{reporting.executive_summary}</p>
			</Card>

			<Card title="Monthly & Quarterly Test Reports" icon="▦" right="tested -> result -> intelligence impact">
				<div className="cop-period-report-grid">
					{reporting.periodic_reports.map((report) => (
						<section className="cop-period-report" key={report.cadence}>
							<div className="cop-period-head">
								<div>
									<div className="cop-period-kicker">{report.cadence} report</div>
									<h4>{report.period}</h4>
								</div>
								<Pill tone={report.status === 'client-ready' ? 'green' : 'purple'}>{report.status}</Pill>
							</div>
							<p>{report.summary}</p>
							<div className="cop-test-report-list">
								{report.tests.map((test) => (
									<div className="cop-test-report-row" key={test.tested}>
										<div>
											<span>Tested</span>
											<b>{test.tested}</b>
										</div>
										<div>
											<span>Result</span>
											<b>{test.result}</b>
										</div>
										<div>
											<span>Impact</span>
											<b>{test.impact}</b>
										</div>
									</div>
								))}
							</div>
						</section>
					))}
				</div>
			</Card>

			{isInternal && (
				<InternalCard title="Measurement Caveats" icon="∿" right="review before presenting these numbers">
					{measurementCaveats.map((c, i) => (
						<WarnRow key={i} level={c.level}>{c.text}</WarnRow>
					))}
					<div className="cop-aop-links">
						<AopLink view="reports">reporting_bot drafts</AopLink>
					</div>
				</InternalCard>
			)}

			<div className="cop-grid cop-grid-2">
				<Card title="Campaign Effectiveness" icon="▤" right={reportDateRangeLabel}>
					<div className="cop-table-wrap">
					<table className="cop-table">
						<thead>
							<tr>
								<th>Campaign</th>
								<th>Segment</th>
								<th>Revenue</th>
								<th>ROI</th>
								<th>Conv.</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{reporting.campaigns.map((c) => (
								<tr key={c.name}>
									<td><b>{c.name}</b></td>
									<td style={{ color: 'var(--text-sec)' }}>{c.family}</td>
									<td className="cop-num">{c.revenue}</td>
									<td className="cop-num tone-green">{c.roi}</td>
									<td className="cop-num">{c.conv}</td>
									<td><StatusPill status={c.status} /></td>
								</tr>
							))}
						</tbody>
					</table>
					</div>
				</Card>

				<Card title="Conversion Funnel" icon="▼" right="all traffic">
					{reporting.funnel.map((f) => (
						<div className="cop-funnel-row" key={f.stage}>
							<span style={{ color: 'var(--text-sec)' }}>{f.stage}</span>
							<div className="cop-funnel-bar" style={{ width: `${f.pct}%` }} />
							<span className="cop-mono" style={{ textAlign: 'right', color: 'var(--text-sec)' }}>
								{f.value.toLocaleString()} · {f.pct}%
							</span>
						</div>
					))}
					<div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-dim)' }}>
						Checkout → purchase loses 41% — the active Checkout Recovery bandit targets this step.
					</div>
				</Card>
			</div>

			<Card title="Next Best Opportunities" icon="◇" right="ranked by projected incremental revenue">
				<div className="cop-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
					{reporting.opportunities.map((o) => (
						<div className="cop-opp-card" key={o.title}>
							<h4>{o.title}</h4>
							<p>{o.detail}</p>
							<Confidence value={o.confidence} />
							<div className="cop-opp-foot">
								<span className="cop-opp-proj">{o.projected}</span>
								<button className="cop-btn small primary" onClick={() => navigate('builder')}>Launch →</button>
							</div>
						</div>
					))}
				</div>
			</Card>

			<div className="cop-grid cop-grid-2">
				<Card title="New Segments to Target" icon="✣" right={<button className="cop-link" onClick={() => navigate('clustering')}>Clustering Studio →</button>}>
					<div className="cop-table-wrap">
					<table className="cop-table">
						<thead>
							<tr>
								<th>Segment</th>
								<th>Size</th>
								<th>Conv.</th>
								<th>Est. value</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{reporting.new_segments.map((s) => (
								<tr key={s.name}>
									<td><b>{s.name}</b></td>
									<td className="cop-num">{s.size.toLocaleString()}</td>
									<td className="cop-num">{s.conv}</td>
									<td className="cop-num tone-green">{s.value}</td>
									<td><Pill tone={s.badge === 'High Value' ? 'pink' : 'dim'}>{s.badge}</Pill></td>
								</tr>
							))}
						</tbody>
					</table>
					</div>
				</Card>

				<Card title="Theories to Test" icon="❓" right="AI-generated, evidence-linked">
					{reporting.theories.map((t) => (
						<div className="cop-pending-item" key={t.theory}>
							<div className="cop-pending-text" style={{ whiteSpace: 'normal' }}>
								<div className="t" style={{ whiteSpace: 'normal' }}>{t.theory}</div>
								<div className="s">{t.basis}</div>
							</div>
							<button className="cop-btn small" onClick={() => navigate('experiment-lab')}>
								Design Experiment
							</button>
						</div>
					))}
				</Card>
			</div>
		</main>
	);
}
